import { Outlet, Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Link,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import ViewSidebarIcon from '@mui/icons-material/ViewSidebar';
import { useTranslation } from 'react-i18next';
import { NavigationDrawer } from '@components/NavigationDrawer';
import UserMenu from '@components/UserMenu';
import Breadcrumbs from '@components/Breadcrumbs';
import ErrorBoundary from '@components/ErrorBoundary';
import LanguageSelector from '@components/LanguageSelector';
import OrgSwitcher from '@components/OrgSwitcher';
import { useUIStore, useAuthStore } from '@store/index';

function MainLayout() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // REV-724: the one control drives whichever drawer is actually on screen.
  const { sidebarOpen, toggleSidebar, mobileNavOpen, toggleMobileNav } = useUIStore();
  const navOpen = isMobile ? mobileNavOpen : sidebarOpen;
  const toggleNav = isMobile ? toggleMobileNav : toggleSidebar;
  const { isAuthenticated } = useAuthStore();
  // REV-588: keys the error boundary so a route change clears a caught error.
  const location = useLocation();

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Skip link for keyboard users */}
      <Link href="#main-content" className="skip-link">
        {t('common.skipToContent')}
      </Link>

      {/* Sidebar Navigation */}
      {isAuthenticated && <NavigationDrawer />}

      {/* Main content wrapper */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minWidth: 0, // Prevent flex item from overflowing
          transition: theme.transitions.create(['margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        {/* Header */}
        <AppBar
          position="sticky"
          component="header"
          elevation={1}
          sx={{
            backgroundColor: 'background.paper',
            color: 'text.primary',
          }}
        >
          <Toolbar>
            {/* Sidebar toggle for mobile or when authenticated.
                REV-569: a side-panel icon, not a hamburger. A hamburger
                conventionally opens a navigation MENU; this control collapses
                and expands a panel, which is a different promise. The
                accessible name said "navigation menu" too, so the wrong
                metaphor reached screen-reader users as well as sighted ones —
                both now describe showing and hiding a sidebar. */}
            {(isMobile || isAuthenticated) && (
              <IconButton
                edge="start"
                aria-label={navOpen ? t('navigation.hideSidebar') : t('navigation.showSidebar')}
                onClick={toggleNav}
                sx={{ mr: 2 }}
              >
                <ViewSidebarIcon />
              </IconButton>
            )}

            {/* App name / Logo.
                REV-565: a span, not an h1. The product name is the site's
                identity, not the page's heading — as an h1 it meant every
                authenticated page had two, and a screen-reader user navigating
                by heading landed on "Project Revenant" before the thing they
                actually opened. The page title is now the single h1.
                NavigationDrawer already renders this name as a span; this
                brings the banner into line with it. */}
            <Typography
              variant="h6"
              component="span"
              sx={{
                flexGrow: 1,
                fontWeight: 700,
                color: 'primary.main',
              }}
            >
              {t('common.appName')}
            </Typography>

            {/* Right side actions */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {isAuthenticated && <OrgSwitcher />}
              <LanguageSelector />
              <UserMenu />
            </Box>
          </Toolbar>
        </AppBar>

        {/* Main content area */}
        <Container
          component="main"
          id="main-content"
          tabIndex={-1}
          maxWidth="xl"
          sx={{
            flex: 1,
            py: 3,
            px: { xs: 2, sm: 3 },
          }}
        >
          {/* Breadcrumb navigation */}
          <Breadcrumbs />

          {/* Page content.
              REV-588: inside <main> and BELOW the breadcrumbs on purpose. A
              render error then replaces only the page, leaving the app bar,
              navigation, breadcrumbs and footer intact — so the user can still
              navigate out. Keyed on the pathname so moving to another route
              clears the error instead of latching it over every later page. */}
          <ErrorBoundary resetKey={location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </Container>

        {/* Footer */}
        <Box
          component="footer"
          sx={{
            py: 2,
            px: 2,
            mt: 'auto',
            backgroundColor: 'background.default',
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          <Container maxWidth="xl">
            <Box sx={{ textAlign: 'center', mb: 1 }}>
              <Link component={RouterLink} to="/help" variant="body2" color="text.secondary">
                {t('help.navLabel')}
              </Link>
            </Box>
            <Box
              component="nav"
              aria-label={t('legal.footerHeading')}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                flexWrap: 'wrap',
                gap: { xs: 1, sm: 3 },
                mb: 1,
              }}
            >
              <Link component={RouterLink} to="/legal/terms" variant="body2" color="text.secondary">
                {t('legal.terms')}
              </Link>
              <Link
                component={RouterLink}
                to="/legal/privacy"
                variant="body2"
                color="text.secondary"
              >
                {t('legal.privacy')}
              </Link>
              <Link component={RouterLink} to="/legal/dpa" variant="body2" color="text.secondary">
                {t('legal.dpa')}
              </Link>
            </Box>
            <Typography variant="body2" color="text.secondary" align="center">
              {t('common.copyright', '© {{year}} AFixt.', { year: new Date().getFullYear() })}{' '}
              {t('common.allRightsReserved')}
            </Typography>
          </Container>
        </Box>
      </Box>
    </Box>
  );
}

export default MainLayout;
